import React, { useState, useEffect, useRef } from 'react';
import { Filter, ArrowUpAZ, ArrowDownAZ, Check } from 'lucide-react';

const FilterableDataGrid = ({
  data,
  allData, // Full unfiltered data for calculating filter options
  columns: initialColumns,
  onRowClick,
  onRowDoubleClick,
  filters,
  setFilters,
  // Column customization props (external state from header)
  columnOrder: externalColumnOrder,
  hiddenColumns: externalHiddenColumns,
  columnWidths: externalColumnWidths,
  onColumnResize,
  // Edit mode props
  editModeEnabled = false,
  editedData = {},           // { rowId: { fieldName: value } }
  renderEditableCell,        // (row, column, value, isEdited) => JSX
  // Delete mode props
  deleteModeEnabled = false,
  selectedForDelete = new Set(),
  onToggleSelectRow,
  onSelectAllOnPage,
  // Frozen columns
  frozenColumns = []
}) => {
  // Use allData for filter options if provided, otherwise fall back to data
  const optionsData = allData || data;
  const [openFilter, setOpenFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState({});
  const [pendingSelections, setPendingSelections] = useState({});
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const filterRef = useRef(null);
  const headerRefs = useRef({});

  // Column customization state - use external if provided, otherwise internal
  const [internalColumnOrder, setInternalColumnOrder] = useState(() => initialColumns.map(col => col.key));
  const [internalHiddenColumns] = useState(new Set());
  const [internalColumnWidths, setInternalColumnWidths] = useState(() => {
    const widths = {};
    initialColumns.forEach(col => {
      widths[col.key] = col.width || 150;
    });
    return widths;
  });

  // Column resize state
  const [resizing, setResizing] = useState(null); // { key, startX, startWidth }
  const tableRef = useRef(null);

  // Use external state if provided (and non-empty), otherwise use internal
  const columnOrder = (externalColumnOrder?.length > 0) ? externalColumnOrder : internalColumnOrder;
  const hiddenColumns = (externalHiddenColumns?.length > 0) ? new Set(externalHiddenColumns) : internalHiddenColumns;
  const columnWidths = externalColumnWidths || internalColumnWidths;

  // Update internal column order when initialColumns change (only if using internal state)
  useEffect(() => {
    if (!externalColumnOrder?.length) {
      setInternalColumnOrder(prev => {
        const newKeys = initialColumns.map(col => col.key);
        const prevSet = new Set(prev);
        const newSet = new Set(newKeys);
        // If the set of keys changed, reset to initialColumns order (respects upstream ordering)
        const keysChanged = newKeys.some(k => !prevSet.has(k)) || prev.some(k => !newSet.has(k));
        if (!keysChanged) return prev;
        return newKeys;
      });
    }
  }, [initialColumns, externalColumnOrder]);

  // Get visible columns in current order
  const visibleColumns = columnOrder
    .filter(key => !hiddenColumns.has(key))
    .map(key => initialColumns.find(col => col.key === key))
    .filter(Boolean);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setOpenFilter(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Column resize handlers
  const handleResizeStart = (e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[colKey] || 150;
    setResizing({ key: colKey, startX: e.clientX, startWidth });
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const diff = e.clientX - resizing.startX;
      const newWidth = Math.max(50, resizing.startWidth + diff); // Min width 50px

      if (onColumnResize) {
        onColumnResize(resizing.key, newWidth);
      } else {
        setInternalColumnWidths(prev => ({
          ...prev,
          [resizing.key]: newWidth
        }));
      }
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, onColumnResize]);

  const getColumnOptions = (colKey) => {
    const otherFilters = Object.entries(filters).filter(([key]) => key !== colKey);
    const baseData = optionsData.filter(row =>
      otherFilters.every(([key, vals]) => !vals || vals.length === 0 || vals.includes(String(row[key] || '')))
    );
    return [...new Set(baseData.map(row => String(row[colKey] || '')))].sort();
  };

  const getFilteredOptions = (colKey) => {
    const options = getColumnOptions(colKey);
    const search = (filterSearch[colKey] || '').toLowerCase();
    return search ? options.filter(opt => opt.toLowerCase().includes(search)) : options;
  };

  const openFilterDropdown = (colKey) => {
    const header = headerRefs.current[colKey];
    if (header) {
      const rect = header.getBoundingClientRect();
      const dropdownHeight = 380;
      const dropdownWidth = 240;

      let top = rect.bottom + 4;
      let left = rect.left;

      if (top + dropdownHeight > window.innerHeight) {
        top = rect.top - dropdownHeight - 4;
      }

      if (left + dropdownWidth > window.innerWidth) {
        left = window.innerWidth - dropdownWidth - 8;
      }

      setDropdownPosition({ top, left });
    }
    setPendingSelections(prev => ({ ...prev, [colKey]: filters[colKey] || [] }));
    setOpenFilter(colKey);
  };

  const handleCheckboxChange = (colKey, value) => {
    setPendingSelections(prev => {
      const current = prev[colKey] || [];
      return {
        ...prev,
        [colKey]: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value]
      };
    });
  };

  const handleSelectAll = (colKey) => {
    const allOptions = getFilteredOptions(colKey);
    const currentSelections = pendingSelections[colKey] || [];
    const allSelected = allOptions.every(opt => currentSelections.includes(opt));
    
    if (allSelected) {
      // Deselect all filtered options
      setPendingSelections(prev => ({
        ...prev,
        [colKey]: currentSelections.filter(sel => !allOptions.includes(sel))
      }));
    } else {
      // Select all filtered options
      setPendingSelections(prev => ({
        ...prev,
        [colKey]: [...new Set([...currentSelections, ...allOptions])]
      }));
    }
  };

  const applyFilter = (colKey) => {
    const selected = pendingSelections[colKey] || [];
    const allOptions = getColumnOptions(colKey);
    // If all options selected, clear the filter (no filtering needed)
    const isAllSelected = allOptions.length > 0 && allOptions.every(opt => selected.includes(opt));
    setFilters(prev => ({ ...prev, [colKey]: isAllSelected ? [] : selected }));
    setOpenFilter(null);
  };

  const cancelFilter = () => setOpenFilter(null);

  const getCurrentSelections = (colKey) => {
    return openFilter === colKey ? (pendingSelections[colKey] || []) : (filters[colKey] || []);
  };

  const isAllSelected = (colKey) => {
    const allOptions = getFilteredOptions(colKey);
    const currentSelections = getCurrentSelections(colKey);
    return allOptions.length > 0 && allOptions.every(opt => currentSelections.includes(opt));
  };

  const handleSort = (colKey, direction) => {
    setSortConfig({ key: colKey, direction });
    setOpenFilter(null);
  };

  // Filter data based on column filters
  const getFilteredData = () => {
    if (!filters || Object.keys(filters).length === 0) return data;

    return data.filter(row => {
      return Object.entries(filters).every(([colKey, selectedValues]) => {
        if (!selectedValues || selectedValues.length === 0) return true;
        const cellValue = String(row[colKey] || '');
        return selectedValues.includes(cellValue);
      });
    });
  };

  const filteredData = getFilteredData();

  const getSortedData = () => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = String(a[sortConfig.key] || '').toLowerCase();
      const bVal = String(b[sortConfig.key] || '').toLowerCase();

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedData = getSortedData();

  // eslint-disable-next-line no-unused-vars
  const hasActiveFilters = filters && Object.values(filters).some(f => f?.length > 0);

  // Calculate total table width from column widths (add 40px for delete checkbox column if enabled)
  const tableWidth = visibleColumns.reduce((sum, c) => sum + (columnWidths[c.key] || c.width || 150), 0) + (deleteModeEnabled ? 40 : 0);

  // Compute left offsets for sticky frozen columns
  const frozenSet = new Set(frozenColumns);
  const frozenLeftOffsets = {};
  if (frozenSet.size > 0) {
    let leftAccum = deleteModeEnabled ? 40 : 0;
    visibleColumns.forEach(col => {
      if (frozenSet.has(col.key)) frozenLeftOffsets[col.key] = leftAccum;
      leftAccum += columnWidths[col.key] || col.width || 150;
    });
  }

  // Check if all visible rows are selected for delete
  const allOnPageSelected = deleteModeEnabled && sortedData.length > 0 && sortedData.every(row => selectedForDelete.has(row.id));

  return (
    <div className={`data-grid-container ${resizing ? 'is-resizing' : ''}`}>
      <div className="data-grid-scroll-area">
      <table ref={tableRef} className={`data-grid${editModeEnabled ? ' edit-mode' : ''}${deleteModeEnabled ? ' delete-mode' : ''}`} style={{ tableLayout: 'fixed', width: tableWidth + 'px' }}>
        <colgroup>
          {deleteModeEnabled && <col style={{ width: '40px' }} />}
          {visibleColumns.map(col => (
            <col key={col.key} style={{ width: `${columnWidths[col.key] || col.width || 150}px` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {deleteModeEnabled && (
              <th className="delete-checkbox-col">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={onSelectAllOnPage}
                  title="Select all on page"
                />
              </th>
            )}
            {visibleColumns.map(col => {
              const hasFilter = filters[col.key]?.length > 0;
              const isSorted = sortConfig.key === col.key;
              const isHovered = hoveredColumn === col.key;
              const isVertical = col.headerVertical;
              const isFrozen = frozenSet.has(col.key);
              const thClasses = [
                isVertical ? 'header-rotate-90' : '',
                'filterable-header',
                isFrozen ? 'frozen' : ''
              ].filter(Boolean).join(' ');
              const frozenStyle = isFrozen ? { position: 'sticky', left: frozenLeftOffsets[col.key], zIndex: 4 } : {};
              return (
                <th
                  key={col.key}
                  ref={el => headerRefs.current[col.key] = el}
                  className={thClasses}
                  style={frozenStyle}
                  onClick={() => openFilterDropdown(col.key)}
                  onMouseEnter={() => setHoveredColumn(col.key)}
                  onMouseLeave={() => setHoveredColumn(null)}
                >
                  <span className={`column-label ${isVertical ? 'header-content' : ''}`}>{col.label}</span>
                  <Filter
                    size={12}
                    className={`header-filter-icon ${isHovered || hasFilter || isSorted ? 'visible' : ''} ${hasFilter || isSorted ? 'active' : ''}`}
                  />
                  <div
                    className="column-resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, col.key)}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, idx) => {
            const isSelectedForDelete = deleteModeEnabled && selectedForDelete.has(row.id);
            return (
            <tr
              key={row.id || idx}
              onClick={() => onRowClick?.(row)}
              onDoubleClick={() => onRowDoubleClick?.(row)}
              className={`${onRowClick || onRowDoubleClick ? 'clickable' : ''}${isSelectedForDelete ? ' selected-for-delete' : ''}`}
            >
              {deleteModeEnabled && (
                <td className="delete-checkbox-col">
                  <input
                    type="checkbox"
                    checked={isSelectedForDelete}
                    onChange={() => onToggleSelectRow?.(row.id)}
                  />
                </td>
              )}
              {visibleColumns.map(col => {
                const value = row[col.key];
                const rowId = row.id;
                const isFrozen = frozenSet.has(col.key);
                const frozenStyle = isFrozen ? { position: 'sticky', left: frozenLeftOffsets[col.key], zIndex: 1 } : {};
                const cellStyle = { textAlign: col.alignment || 'left', ...frozenStyle };
                const frozenClass = isFrozen ? ' frozen' : '';
                const isEdited = editedData[rowId]?.[col.fieldName] !== undefined;
                const displayValue = isEdited ? editedData[rowId][col.fieldName] : value;

                // Edit mode: use custom renderer if provided
                if (editModeEnabled && renderEditableCell) {
                  return (
                    <td key={col.key} className={`editable-cell${isEdited ? ' modified' : ''}${frozenClass}`} style={cellStyle}>
                      {renderEditableCell(row, col, displayValue, isEdited)}
                    </td>
                  );
                }

                // Read mode: render boolean values as checkboxes
                if (typeof value === 'boolean') {
                  return (
                    <td key={col.key} className={`cell-boolean${frozenClass}`} style={cellStyle}>
                      <div className="boolean-display">
                        {value ? <Check size={16} className="boolean-check" /> : null}
                      </div>
                    </td>
                  );
                }
                const renderedValue = col.formatter ? col.formatter(value) : value;
                return <td key={col.key} className={frozenClass || undefined} style={cellStyle}>{renderedValue}</td>;
              })}
            </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {openFilter && (
        <div
          ref={filterRef}
          className="filter-dropdown"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          onClick={e => e.stopPropagation()}
        >
          <div className="filter-sort-section">
            <button 
              className={`filter-sort-btn ${sortConfig.key === openFilter && sortConfig.direction === 'asc' ? 'active' : ''}`}
              onClick={() => handleSort(openFilter, 'asc')}
            >
              <ArrowUpAZ size={16} />
              <span>Sort A to Z</span>
            </button>
            <button 
              className={`filter-sort-btn ${sortConfig.key === openFilter && sortConfig.direction === 'desc' ? 'active' : ''}`}
              onClick={() => handleSort(openFilter, 'desc')}
            >
              <ArrowDownAZ size={16} />
              <span>Sort Z to A</span>
            </button>
          </div>
          
          <div className="filter-divider" />
          
          <div className="filter-search">
            <input
              type="text"
              placeholder="Search"
              value={filterSearch[openFilter] || ''}
              onChange={e => setFilterSearch(prev => ({
                ...prev,
                [openFilter]: e.target.value
              }))}
            />
          </div>
          
          <div className="filter-options">
            <label className="filter-option filter-option-select-all">
              <input
                type="checkbox"
                checked={isAllSelected(openFilter)}
                onChange={() => handleSelectAll(openFilter)}
              />
              <span>(Select All)</span>
            </label>
            {getFilteredOptions(openFilter).map(option => (
              <label key={option} className="filter-option">
                <input
                  type="checkbox"
                  checked={getCurrentSelections(openFilter).includes(option)}
                  onChange={() => handleCheckboxChange(openFilter, option)}
                />
                <span>{option || '(empty)'}</span>
              </label>
            ))}
          </div>
          
          <div className="filter-footer">
            <button onClick={() => applyFilter(openFilter)} className="filter-ok-btn">
              OK
            </button>
            <button onClick={cancelFilter} className="filter-cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterableDataGrid;
